import { DeleteOutlined, HolderOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { Button, Input, Modal, Tag, Typography, theme } from 'antd';
import type React from 'react';
import { type ReactNode, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { LazySortableRows } from '../../../components/LazySortableRows.tsx';
import { LoadingIndicator } from '../../../components/LoadingIndicator.tsx';
import { type Localization, type LocalizationFieldValue, type SelectedParameter, useAppStore } from '../../../hooks/store/useAppStore';
import { MAX_LOCALIZATION_LENGTH } from '../../../utils/localizationLimits.ts';
import { LocalizationFieldInputs } from './LocalizationFieldInputs.tsx';
import { LOCALIZATIONS_CHOOSER_QUERY } from './LocalizationsChooserModal.gql';
import { RecordedFieldValues } from './RecordedFieldValues.tsx';
import { type LocalizationError, validateLocalizations } from './validateLocalizations.ts';

export type LocalizationsChooserModalProps = {
  selectedParams: SelectedParameter[];
  flipParamNames: boolean;
  requestIdForReorder: string;
  open: boolean;
  okText: string;
  okIcon: ReactNode;
  closable: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * One physical tube: a Material and a Lokalisation, the analyses it is taken for, and the values
 * recorded once for all of them.
 *
 * The store keeps a flat row per (analysis, Material, Lokalisation) because that is what the API
 * takes, but the Etikett is keyed without the analysis and carries `paramShortNames` as a set - so
 * the values belong to the tube, not to the analysis. Editing per analysis meant showing the same
 * tube once per analysis with silently linked values.
 */
type TubeGroup = {
  specimenId: string;
  location: string;
  freeText: boolean;
  fieldValues: LocalizationFieldValue[];
  /** Analyses this tube is taken for. Empty while the user is moving it between analyses. */
  paramIds: string[];
};

export const LocalizationsChooserModal: React.FC<LocalizationsChooserModalProps> = ({
  selectedParams,
  flipParamNames,
  requestIdForReorder,
  open,
  okText,
  okIcon,
  closable,
  onClose,
  onSuccess,
}) => {
  const { token } = theme.useToken();

  const [errors, setErrors] = useState<LocalizationError[]>([]);
  const [okClicked, setOkClicked] = useState(false);
  const [freeTextDrafts, setFreeTextDrafts] = useState<Record<string, string>>({});
  /**
   * Tubes the user has unticked from every analysis. They cannot live in the store - a row there is
   * keyed by analysis - but dropping them would throw away the values that were typed into them, so
   * they wait here until an analysis is ticked again or the tube is deleted.
   */
  const [unassigned, setUnassigned] = useState<TubeGroup[]>([]);
  /**
   * The sequence of Lokalisationen per Material, held here rather than read off the store: a tube
   * with no analysis has no rows there, so deriving the order from the store alone would make a
   * block jump to the end the moment its last analysis is unticked.
   */
  const [order, setOrder] = useState<Record<string, string[]>>({});

  const { localizations, setLocalizations } = useAppStore(
    useShallow(state => ({
      localizations: state.localizations,
      setLocalizations: state.setLocalizations,
    }))
  );

  const { data, loading } = useQuery(LOCALIZATIONS_CHOOSER_QUERY, {
    variables: { id: requestIdForReorder },
    skip: !requestIdForReorder,
  });

  /**
   * What was recorded when the sample was taken. On a Nachforderung these are not editable - the
   * label is reused - but they are what tells two Lokalisationen of the same Material apart.
   */
  const recordedFieldValues = (specimenId: string, location: string) =>
    (data?.request?.orders ?? []).flatMap(it => it.orderLabels).find(it => it.specimenId === specimenId && it.location === location)
      ?.localizationFieldValues ?? [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-evaluate whenever the modal opens or closes
  useEffect(() => {
    // Errors are computed on open so the OK handler has them, but stay hidden until the user asks
    // to continue: a dialog that opens with every field already red tells nobody anything.
    setErrors(open ? validateLocalizations(selectedParams, localizations, !!requestIdForReorder, flipParamNames) : []);
    setOkClicked(false);
    setUnassigned([]);
    setOrder({});
  }, [open]);

  /** Every Material that needs Lokalisationen, once, with the analyses that use it. */
  const materials = selectedParams
    .flatMap(param => param.specimens.filter(it => it.requiresLocalizations).map(spec => ({ param, spec })))
    .reduce<{ spec: SelectedParameter['specimens'][number]; params: SelectedParameter[] }[]>((acc, { param, spec }) => {
      const known = acc.find(it => it.spec.id === spec.id);
      if (known) {
        known.params.push(param);
      } else {
        acc.push({ spec, params: [param] });
      }
      return acc;
    }, []);

  /** The store's flat rows, folded back into tubes, plus the ones no analysis points at. */
  const tubesOf = (specimenId: string): TubeGroup[] => {
    const grouped: TubeGroup[] = [];
    for (const row of localizations.filter(it => it.specimenId === specimenId)) {
      const known = grouped.find(it => it.location === row.location);
      if (known) {
        known.paramIds.push(row.paramId);
      } else {
        grouped.push({ ...row, paramIds: [row.paramId] });
      }
    }

    const tubes = [...grouped, ...unassigned.filter(it => it.specimenId === specimenId)];
    const known = order[specimenId];
    if (!known) {
      return tubes;
    }
    // recorded sequence first, anything the record does not know keeps its relative position
    const rank = (location: string) => {
      const index = known.indexOf(location);
      return index === -1 ? known.length : index;
    };
    return tubes
      .map((tube, index) => ({ tube, index }))
      .sort((a, b) => rank(a.tube.location) - rank(b.tube.location) || a.index - b.index)
      .map(it => it.tube);
  };

  const apply = (next: Localization[]) => {
    setErrors(validateLocalizations(selectedParams, next, !!requestIdForReorder, flipParamNames));
    setLocalizations(next);
  };

  /**
   * Replaces one Material's rows with the tubes given, keeping every other Material untouched.
   * Only the order *within* a Material and analysis is read downstream, so appending is safe.
   */
  const writeTubes = (specimenId: string, tubes: TubeGroup[]) => {
    const rows = tubes.flatMap(tube =>
      tube.paramIds.map(paramId => ({
        paramId,
        specimenId: tube.specimenId,
        location: tube.location,
        freeText: tube.freeText,
        fieldValues: tube.fieldValues,
      }))
    );
    apply([...localizations.filter(it => it.specimenId !== specimenId), ...rows]);
    setUnassigned([...unassigned.filter(it => it.specimenId !== specimenId), ...tubes.filter(it => !it.paramIds.length)]);
    setOrder({ ...order, [specimenId]: tubes.map(it => it.location) });
  };

  const onOk = () => {
    setOkClicked(true);
    const found = validateLocalizations(selectedParams, localizations, !!requestIdForReorder, flipParamNames);
    setErrors(found);
    if (found.length || unassigned.length) {
      return;
    }
    onSuccess();
  };

  /**
   * Adds a Lokalisation for every analysis of this Material - that is what ticking it in the list
   * means. Predefined ones slot in so the predefined entries keep the configured anatomical order
   * among themselves; free text is appended and can be dragged anywhere afterwards.
   */
  const addLocation = (specimenId: string, location: string, predefined: readonly string[], paramIds: string[]) => {
    const own = tubesOf(specimenId);
    if (own.some(it => it.location === location)) {
      return;
    }

    const tube: TubeGroup = {
      specimenId,
      location,
      freeText: !predefined.includes(location),
      fieldValues: recordedFieldValues(specimenId, location).map(it => ({ label: it.label, value: it.value })),
      paramIds,
    };

    const configIndex = predefined.indexOf(location);
    let insertAt = own.length;
    if (configIndex !== -1) {
      const before = own.findIndex(it => {
        const otherIndex = predefined.indexOf(it.location);
        return otherIndex !== -1 && otherIndex > configIndex;
      });
      if (before !== -1) {
        insertAt = before;
      }
    }

    writeTubes(specimenId, [...own.slice(0, insertAt), tube, ...own.slice(insertAt)]);
  };

  const removeLocation = (specimenId: string, location: string) =>
    writeTubes(
      specimenId,
      tubesOf(specimenId).filter(it => it.location !== location)
    );

  const toggleAnalysis = (specimenId: string, location: string, paramId: string) =>
    writeTubes(
      specimenId,
      tubesOf(specimenId).map(it =>
        it.location !== location
          ? it
          : {
              ...it,
              paramIds: it.paramIds.includes(paramId) ? it.paramIds.filter(id => id !== paramId) : [...it.paramIds, paramId],
            }
      )
    );

  const setFieldValues = (specimenId: string, location: string, fieldValues: LocalizationFieldValue[]) =>
    writeTubes(
      specimenId,
      tubesOf(specimenId).map(it => (it.location !== location ? it : { ...it, fieldValues }))
    );

  const bodyStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${token.margin}px;
    width: 100%;
    max-height: 60vh;
    overflow-y: auto;
    /* overflow-y makes the other axis clip too, which cuts the focus ring off anything sitting
       against an edge - the padding gives it room, the negative margin keeps the alignment */
    padding: ${token.controlOutlineWidth}px;
    margin: -${token.controlOutlineWidth}px;
  `;

  const materialStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${token.marginXS}px;

    & + & {
      margin-top: ${token.marginXS}px;
      padding-top: ${token.marginXS}px;
      border-top: 1px dashed ${token.colorSplit};
    }
  `;

  /** Name on its own line, options below: side by side the name gets pushed around as soon as a
   *  Material has more than a handful of Lokalisationen. */
  const materialHeadStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${token.marginXXS}px;
  `;

  const materialNameRowStyle = css`
    display: flex;
    align-items: baseline;
    gap: ${token.marginXS}px;
  `;

  const pickerStyle = css`
    display: flex;
    flex-wrap: wrap;
    gap: ${token.marginXXS}px;
    align-items: center;

    /* The row that decides which Etiketten exist, so it carries the Material's own text size. */
    .ant-tag-checkable {
      font-size: ${token.fontSize}px;
      line-height: ${token.lineHeightSM};
      padding-inline: ${token.paddingSM}px;
    }

    /* An unchecked CheckableTag is bare text in antd, which reads as a label rather than as an
       option. Give it a surface so the row looks like the set of choices it is. */
    .ant-tag-checkable:not(.ant-tag-checkable-checked) {
      background: ${token.colorFillQuaternary};
      border-color: ${token.colorBorderSecondary};
      color: ${token.colorTextSecondary};
    }

    .ant-tag-checkable:not(.ant-tag-checkable-checked):hover {
      background: ${token.colorFillSecondary};
    }
  `;

  /** Chip-shaped so it reads as one more option in the row, not as a detached form below it -
   *  including the corner, which has to be the Tags' and not the Input's default. */
  const freeTextInputStyle = css`
    width: 168px;
    border-radius: ${token.borderRadiusSM}px;
  `;

  const tubeListStyle = css`
    display: flex;
    flex-direction: column;
    gap: ${token.marginXXS}px;
  `;

  /** Grip column inside the border, so it is visibly part of the block it moves. */
  const tubeStyle = css`
    display: grid;
    grid-template-columns: ${token.controlHeightSM}px minmax(0, 1fr);
    align-items: stretch;
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadius}px;
    /* opaque, not one of the translucent colorFill tokens - a block being dragged would otherwise
       show the row underneath it */
    background: ${token.colorBgLayout};
    overflow: hidden;
  `;

  /** Marks the block being moved by its edge. Deliberately no shadow and no transparency - the
   *  block stays flat and readable over whatever it passes. */
  const tubeDraggingStyle = css`
    border-color: ${token.colorPrimaryBorder};
  `;

  /** Same colour as every other blocker in this dialog - it stops the submit just as they do. */
  const tubeInvalidStyle = css`
    border-color: ${token.colorError};
  `;

  const gripStyle = css`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${token.colorTextQuaternary};
  `;

  /** The whole bar is the grip, not just the icon - a 24px column is a far easier target than a
   *  16px glyph, and it is what the coloured band already looks like it does. A fixed row keeps
   *  the column width so the block edges line up. */
  const gripMovableStyle = css`
    background: ${token.colorFillTertiary};
    border-right: 1px solid ${token.colorBorderSecondary};
    cursor: move;
    touch-action: none;
    color: ${token.colorTextTertiary};

    &:hover {
      background: ${token.colorFillSecondary};
      color: ${token.colorText};
    }

    &:focus-visible {
      outline: 2px solid ${token.colorPrimaryBorder};
      outline-offset: -2px;
    }
  `;

  const tubeBodyStyle = css`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: ${token.paddingXXS}px ${token.paddingSM}px ${token.paddingXS}px;
  `;

  const tubeHeadStyle = css`
    display: flex;
    align-items: center;
    gap: ${token.marginXS}px;
  `;

  const spacerStyle = css`
    flex: 1 1 auto;
  `;

  /** The analyses this tube is taken for - the shape paramShortNames already has on the Etikett. */
  const forUseStyle = css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: ${token.marginXXS}px;

    /* Selected reads the same way as the Lokalisation picker above - solid primary with the
       light-solid text; never colorPrimaryText on a primary surface, both are blue at this brand's
       dark primary. The two rows are told apart by shape instead: pills qualify the block they sit
       in, the picker's rounded rectangles are its subject. antd's Tag is already fontSizeSM, so
       size alone cannot carry that difference. */
    .ant-tag-checkable {
      line-height: ${token.fontSizeSM + 8}px;
      padding-inline: ${token.paddingXS}px;
      border-radius: 999px;
      background: ${token.colorBgContainer};
      border-color: ${token.colorBorderSecondary};
      color: ${token.colorTextTertiary};
    }

    .ant-tag-checkable-checked {
      background: ${token.colorPrimary};
      border-color: ${token.colorPrimary};
      color: ${token.colorTextLightSolid};
      font-weight: 500;
    }

    /* the checked chip keeps its white text on hover */
    .ant-tag-checkable:not(.ant-tag-checkable-checked):hover {
      border-color: ${token.colorPrimary};
      color: ${token.colorText};
    }
  `;

  const forUseLabelStyle = css`
    font-size: ${token.fontSizeSM}px;
    margin-right: ${token.marginXXS}px;
  `;

  /** Mono is the Kurzname's, because that one is a code - the Langbezeichnung stays proportional. */
  const soleAnalysisStyle = css`
    font-family: ${flipParamNames ? 'inherit' : token.fontFamilyCode};
    font-size: ${token.fontSizeSM}px;
  `;

  /** A Lokalisation with no extra fields is one line - it must not become an empty slab. */
  const tubeBareStyle = css`
    padding-block: 2px;
  `;

  const analysisTagStyle = css`
    font-family: ${flipParamNames ? 'inherit' : token.fontFamilyCode};
    margin-inline-end: 0;
  `;

  return (
    <Modal
      width={760}
      title="Bitte geben Sie Lokalisationen an"
      okText={okText}
      okButtonProps={{ icon: okIcon }}
      closable={closable}
      keyboard={closable}
      maskClosable={closable}
      cancelButtonProps={{ hidden: !closable }}
      open={open}
      onCancel={onClose}
      onOk={onOk}
      destroyOnHidden
    >
      {loading ? (
        <LoadingIndicator height="200px" />
      ) : (
        <div className={bodyStyle}>
          {materials.map(({ spec, params }) => {
            // On a Nachforderung only Lokalisationen that already exist on the initial order can be
            // picked - the sample was taken back then.
            const available = requestIdForReorder
              ? [
                  ...new Set(
                    (data?.request?.orders ?? [])
                      .flatMap(it => it.orderLabels)
                      .filter(it => it.specimenId === spec.id && !!it.location.length)
                      .map(it => it.location)
                  ),
                ]
              : spec.predefinedLocalizations;
            const tubes = tubesOf(spec.id);
            const draft = freeTextDrafts[spec.id] ?? '';
            const paramIds = params.map(it => it.id);
            // With one analysis there is nothing to assign - every tube belongs to it.
            const showAnalyses = params.length > 1;

            const addFreeText = () => {
              const name = draft.trim();
              if (!name) {
                return;
              }
              addLocation(spec.id, name, spec.predefinedLocalizations, paramIds);
              setFreeTextDrafts({ ...freeTextDrafts, [spec.id]: '' });
            };

            return (
              <div key={spec.id} className={materialStyle}>
                <div className={materialHeadStyle}>
                  <div className={materialNameRowStyle}>
                    <Typography.Text strong>{spec.name}</Typography.Text>
                    {/* With one analysis the per-tube chips would be a row of always-checked noise,
                        but the name still has to appear somewhere - here, beside the Material. */}
                    {!showAnalyses && (
                      <Typography.Text type="secondary" className={soleAnalysisStyle}>
                        {flipParamNames ? params[0].longName : params[0].shortName}
                      </Typography.Text>
                    )}
                  </div>
                  <div className={pickerStyle}>
                    {available.map(location => (
                      <Tag.CheckableTag
                        key={location}
                        checked={tubes.some(it => it.location === location)}
                        onChange={checked =>
                          checked
                            ? addLocation(spec.id, location, spec.predefinedLocalizations, paramIds)
                            : removeLocation(spec.id, location)
                        }
                      >
                        {location}
                      </Tag.CheckableTag>
                    ))}
                    {!requestIdForReorder && spec.localizationInputEnabled && (
                      <Input
                        size="small"
                        className={freeTextInputStyle}
                        placeholder="Neue Lokalisation"
                        maxLength={MAX_LOCALIZATION_LENGTH}
                        value={draft}
                        onChange={e => setFreeTextDrafts({ ...freeTextDrafts, [spec.id]: e.target.value })}
                        onPressEnter={e => {
                          e.preventDefault();
                          addFreeText();
                        }}
                        suffix={<Button size="small" type="text" icon={<PlusOutlined />} onClick={addFreeText} disabled={!draft.trim()} />}
                      />
                    )}
                  </div>
                  {okClicked &&
                    errors
                      .filter(err => params.some(param => err.key === `${param.id}_${spec.id}`))
                      .map(err => (
                        <Typography.Text type="danger" key={err.key}>
                          {err.msg}
                        </Typography.Text>
                      ))}
                </div>

                <div className={tubeListStyle}>
                  <LazySortableRows
                    items={tubes}
                    keyOf={item => `${spec.id}_${item.location}`}
                    // AC 2: only free text is placed by hand - the predefined ones keep the
                    // anatomical order configured on the Material
                    isMovable={item => item.freeText}
                    handle="inside"
                    onReorder={items => writeTubes(spec.id, items)}
                    renderRow={(tube, _index, handle) => (
                      <div
                        className={`${tubeStyle} ${handle.dragging ? tubeDraggingStyle : ''} ${
                          tube.paramIds.length ? '' : tubeInvalidStyle
                        }`}
                      >
                        <div className={`${gripStyle} ${handle.movable ? gripMovableStyle : ''}`} {...handle.props}>
                          {handle.movable && <HolderOutlined />}
                        </div>
                        <div className={`${tubeBodyStyle} ${showAnalyses || spec.localizationFields.length ? '' : tubeBareStyle}`}>
                          <div className={tubeHeadStyle}>
                            <Typography.Text strong ellipsis>
                              {tube.location}
                            </Typography.Text>
                            {okClicked && !tube.paramIds.length && (
                              <Typography.Text type="danger">keiner Analyse zugeordnet</Typography.Text>
                            )}
                            <span className={spacerStyle} />
                            <Button
                              size="small"
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => removeLocation(spec.id, tube.location)}
                            />
                          </div>

                          {showAnalyses && (
                            <div className={forUseStyle}>
                              <Typography.Text type="secondary" className={forUseLabelStyle}>
                                für
                              </Typography.Text>
                              {params.map(param => (
                                <Tag.CheckableTag
                                  key={param.id}
                                  className={analysisTagStyle}
                                  checked={tube.paramIds.includes(param.id)}
                                  onChange={() => toggleAnalysis(spec.id, tube.location, param.id)}
                                >
                                  {flipParamNames ? param.longName : param.shortName}
                                </Tag.CheckableTag>
                              ))}
                            </div>
                          )}

                          {requestIdForReorder ? (
                            // The sample was taken with the initial order and its properties were
                            // recorded then. Asking again would collect values the reused label
                            // never accepts, so they are shown rather than edited.
                            <RecordedFieldValues values={recordedFieldValues(spec.id, tube.location)} />
                          ) : (
                            <LocalizationFieldInputs
                              fields={spec.localizationFields}
                              values={tube.fieldValues}
                              showErrors={okClicked}
                              onChange={values => setFieldValues(spec.id, tube.location, values)}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
};
